/**
 * Keeps the page's session token fresh. The cloud mints a short-lived token from the HttpOnly
 * refresh cookie: the page refreshes it before it expires, when the tab becomes visible, and once
 * on a 401. Two tabs that refresh at the same moment need no coordination: the cloud accepts the
 * credential that a rotation replaced for a short time.
 */

import type { AuthSession } from '@/auth/authSession';
import { MINUTE_MS, SECOND_MS } from '@/lib/timeUnits';
import type { RefreshResponse } from '@/types/auth';
import { RefreshOutcome } from '@/types/auth';
import type { Unsubscribe } from '@/types/common';

// Long enough that a request sent just before the refresh still carries a live token.
const REFRESH_LEAD_MS = 5 * MINUTE_MS;
const RETRY_MS = 30 * SECOND_MS;

type TokenListener = (token: string) => void;

interface SessionRefreshDeps {
    authSession: Pick<AuthSession, 'getToken' | 'setToken' | 'wasSignedIn' | 'subscribe'>;
    /** POST /auth/refresh: the answer, or null when the cloud says the session ended. */
    request: () => Promise<RefreshResponse | null>;
    getTokenExpiry: (token: string) => number | null;
    /** The session ended under the page. */
    onEnded: () => void;
    onVisible: (listener: () => void) => Unsubscribe;
    /** Runs the callback after the delay; the answer cancels it. */
    schedule: (callback: () => void, delayMs: number) => Unsubscribe;
    now: () => number;
}

export class SessionRefresher {
    private readonly deps: SessionRefreshDeps;

    private readonly listeners = new Set<TokenListener>();

    private inFlight: Promise<RefreshOutcome> | null = null;

    private cancelTimer: Unsubscribe | null = null;

    /** The expiry of the last token this tab got from a refresh. */
    private mintedExpiry: number | null = null;

    private isStarted = false;

    constructor(deps: SessionRefreshDeps) {
        this.deps = deps;
    }

    /** Keeps the token fresh from now on, for as long as the page is signed in. */
    start(): void {
        if (this.isStarted) {
            return;
        }
        this.isStarted = true;
        this.deps.onVisible((): void => {
            if (this.isDue()) {
                this.refreshInBackground();
            }
        });
        this.deps.authSession.subscribe((): void => {
            this.scheduleNext();
        });
        this.scheduleNext();
    }

    /** Hears each new token. */
    subscribeToken(listener: TokenListener): Unsubscribe {
        this.listeners.add(listener);
        return (): void => {
            this.listeners.delete(listener);
        };
    }

    /** One refresh for all the callers that ask while it runs. */
    refresh(): Promise<RefreshOutcome> {
        if (this.inFlight === null) {
            this.inFlight = this.refreshOnce().finally((): void => {
                this.inFlight = null;
            });
        }
        return this.inFlight;
    }

    private async refreshOnce(): Promise<RefreshOutcome> {
        let answer: RefreshResponse | null;
        try {
            answer = await this.deps.request();
        } catch (error) {
            console.warn('[auth] the session refresh failed', error);
            return RefreshOutcome.Failed;
        }
        if (answer === null) {
            return RefreshOutcome.Ended;
        }
        this.mintedExpiry = answer.expiresAt;
        // A tab that holds its own copy of the token keeps it equal to the cookie.
        if (this.deps.authSession.getToken() !== null) {
            this.deps.authSession.setToken(answer.token);
        }
        this.scheduleNext();
        for (const listener of [...this.listeners]) {
            listener(answer.token);
        }
        return RefreshOutcome.Refreshed;
    }

    private refreshInBackground(): void {
        if (!this.deps.authSession.wasSignedIn()) {
            return;
        }
        this.refresh().then((outcome): void => {
            switch (outcome) {
                case RefreshOutcome.Ended:
                    this.deps.onEnded();
                    break;
                case RefreshOutcome.Failed:
                    this.setTimer(RETRY_MS);
                    break;
                case RefreshOutcome.Refreshed:
                    break;
                default:
                    break;
            }
        });
    }

    private scheduleNext(): void {
        if (!this.deps.authSession.wasSignedIn()) {
            this.clearTimer();
            return;
        }
        const expiresAt = this.getExpiry();
        // A tab that knows no expiry, such as one opened on a cookie session, refreshes at once.
        this.setTimer(expiresAt === null ? 0
            : this.getRefreshTime(expiresAt) - this.deps.now());
    }

    private isDue(): boolean {
        const expiresAt = this.getExpiry();
        return expiresAt === null || this.deps.now() >= this.getRefreshTime(expiresAt);
    }

    /**
     * A token near the session's absolute limit lasts less than the lead: it is refreshed when it
     * expires, and that refresh ends the session.
     */
    private getRefreshTime(expiresAt: number): number {
        const earlyMs = expiresAt - REFRESH_LEAD_MS;
        if (earlyMs <= this.deps.now() && expiresAt === this.mintedExpiry) {
            return expiresAt;
        }
        return earlyMs;
    }

    private getExpiry(): number | null {
        const token = this.deps.authSession.getToken();
        const ownValue = token === null ? null : this.deps.getTokenExpiry(token);
        if (ownValue === null || this.mintedExpiry === null) {
            return ownValue ?? this.mintedExpiry;
        }
        return Math.max(ownValue, this.mintedExpiry);
    }

    private setTimer(delayMs: number): void {
        this.clearTimer();
        this.cancelTimer = this.deps.schedule((): void => {
            this.cancelTimer = null;
            this.refreshInBackground();
        }, Math.max(delayMs, 0));
    }

    private clearTimer(): void {
        this.cancelTimer?.();
        this.cancelTimer = null;
    }
}

/** Calls the listener each time the page becomes visible: timers sleep in a hidden tab. */
export function listenVisible(listener: () => void): Unsubscribe {
    if (typeof document === 'undefined') {
        return (): void => {};
    }
    const onChange = (): void => {
        if (document.visibilityState === 'visible') {
            listener();
        }
    };
    document.addEventListener('visibilitychange', onChange);
    return (): void => {
        document.removeEventListener('visibilitychange', onChange);
    };
}

// A bare setTimeout, called as a method of another object, throws "Illegal invocation".
export function scheduleCancelable(callback: () => void, delayMs: number): Unsubscribe {
    const id = setTimeout(callback, delayMs);
    return (): void => {
        clearTimeout(id);
    };
}
